/**
 * Project Content System
 *
 * This file defines the content for project sub-pages (privacy, support, etc.)
 * Each project can define its own pages that get rendered using shared templates.
 */
import { ENTITY_FORMATION, JURISDICTION, LEGAL_ENTITY, SUPPORT_EMAIL } from './legal';

export interface ContentSection {
  title: string;
  content: string | string[];  // String for paragraphs, array for lists
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface TroubleshootItem {
  title: string;
  steps: string[];
}

export interface ContactInfo {
  email: string;
  responseTime?: string;
}

// Privacy page content structure
export interface PrivacyContent {
  title?: string;
  effectiveDate: string;
  intro: string;
  sections: ContentSection[];
}

// Support page content structure
export interface SupportContent {
  title?: string;
  intro?: string;
  contact: ContactInfo;
  faqs: FAQItem[];
  troubleshooting?: TroubleshootItem[];
  systemRequirements?: string[];
}

// Terms of Service content structure
export interface TermsContent {
  title?: string;
  effectiveDate: string;
  intro: string;
  sections: ContentSection[];
}

// Landing page content (optional override of project data)
export interface LandingContent {
  heroTitle?: string;
  heroSubtitle?: string;
  ctaText?: string;
  ctaUrl?: string;
  additionalSections?: ContentSection[];
}

// All pages a project can have
export interface ProjectPages {
  landing?: LandingContent;
  privacy?: PrivacyContent;
  support?: SupportContent;
  terms?: TermsContent;
}

export const apresNoteContent = {
  privacy: {
    title: 'ApresNote Privacy Policy',
    effectiveDate: 'September 9, 2026',
    intro: `ApresNote is an iPhone meeting app developed by ${LEGAL_ENTITY}, ${ENTITY_FORMATION}. This policy covers the ApresNote app. The company website privacy policy at apresai.dev/privacy covers visits to this website and support email handling.`,
    sections: [
      {
        title: 'Recordings and processing on your iPhone',
        content: [
          'ApresNote records conversations and stores recordings, transcripts, notes, speaker labels, summaries and action items on your iPhone. Speech transcription and speaker voice matching run on the device. There is no cloud speech-to-text service.',
          'Voice embeddings and meeting speaker centroids are stored and processed on your iPhone. They are not sent to our cloud service, Google Gemini or File Search, and are not included in diagnostic uploads.',
          'Enrolled voice profiles may be included in encrypted device backups. Meeting speaker centroids and reference clips are excluded from device backups.',
          'When an installed local language model is selected, its text processing runs on your iPhone. Selecting cloud features is a separate choice.'
        ]
      },
      {
        title: 'Account and service information',
        content: [
          'Sign in with Apple or Google identifies your ApresNote account through Google Identity Platform. Account information includes your account identifier and the name and email supplied for that account. Authentication credentials are kept in the device Keychain.',
          'We use account settings, consent records, subscription verification and credit-usage records to provide the features you select, enforce your choices and maintain your account. Apple processes App Store payments.'
        ]
      },
      {
        title: 'Optional sync',
        content: [
          'Sync is off by default. Enabling it sends meeting text and related records, such as transcripts, summaries, notes, action items and participant information, to your account on Google Cloud.',
          'Turning sync off stops further ordinary meeting uploads and offers to delete the cloud copies. Turning it off without deleting those copies leaves the existing cloud records in place. Your local recordings and text remain on your device.'
        ]
      },
      {
        title: 'Optional cloud AI and search',
        content: [
          'Cloud AI requires separate consent and uses text from synced meetings. Our Google Cloud service sends that text to Google Gemini for the selected summary, chat or naming operation. Cross-meeting Ask and semantic search use Google File Search documents containing meeting text.',
          'Meeting audio, voice embeddings and speaker centroids are never Gemini or File Search inputs. Meeting content is processed through paid Google services under the app’s no-model-training requirement; the free Gemini tier is not used.',
          'Revoking cloud AI consent stops new cloud AI work and deletes the account’s File Search store. Synced meeting records and previously generated summaries remain unless you separately delete them.'
        ]
      },
      {
        title: 'Optional Pro audio backup',
        content: [
          'Audio backup is an optional Pro feature with its own consent. It stores the recording in Google Cloud Storage for playback and restore. Backup is storage, not cloud transcription.',
          'Revoking audio backup deletes stored backup objects after your confirmation. This does not by itself delete meeting text or summaries.'
        ]
      },
      {
        title: 'Calendar and contacts',
        content: [
          'With your permission, ApresNote reads calendar attendees through Apple EventKit. Linking Google Calendar requests read-only access and fetches events on your iPhone. No Google Calendar refresh token is stored on our server.',
          'Calendar attendees and contacts you select can help label speakers. Participant information saved into a meeting follows that meeting’s sync and cloud AI choices. Disconnecting Google Calendar removes that calendar source; it does not remove the EventKit source.'
        ]
      },
      {
        title: 'Deletion, sign-out and recovery records',
        content: [
          'You can delete meetings or request account deletion in the app. Account deletion removes the account’s cloud meeting records, File Search data, backed-up audio and billing records, and revokes the account’s sign-in identity. The app clears that account’s local personal data on the device completing deletion after confirmed server completion.',
          'If deletion is incomplete, the app shows progress and preserves local data for retry. Incomplete deletion progress is retained so the request can be finished.',
          'Access to a completed deletion receipt expires after seven days. A recovery credential held on your device can retrieve deletion status during that window; it cannot restore recordings or meeting text. Receipts contain identifiers, status, timestamps and optional counts, not meeting content. Expired receipts are scheduled for storage cleanup, which is asynchronous.',
          'A minimal account-deletion marker is retained to prevent accidental recreation of a deleted account. It does not contain meeting content. Signing out is different from deleting your account: it clears owned voice identity while preserving recordings and meeting text.'
        ]
      },
      {
        title: 'Diagnostics and information you share',
        content: [
          'ApresNote uses Apple’s MetricKit and Xcode Organizer for diagnostics, with no third-party crash-reporting SDK. Routine logs must not contain unredacted meeting text, participant names in speaker-match details, voice embeddings or authentication secrets.',
          'If you contact support, we receive the information you choose to send. Please do not email recordings, unredacted transcripts, passwords or deletion recovery credentials.'
        ]
      },
      {
        title: 'Changes and contact',
        content: `We may update this policy and its effective date when our practices change. For questions about ApresNote privacy or a deletion request, contact ${SUPPORT_EMAIL}.`
      }
    ]
  },
  terms: {
    title: 'ApresNote Terms of Service',
    effectiveDate: 'September 9, 2026',
    intro: `These Terms govern your use of ApresNote, developed by ${LEGAL_ENTITY}, ${ENTITY_FORMATION}. By downloading or using ApresNote, you agree to these Terms.`,
    sections: [
      {
        title: '1. The app and your recordings',
        content: [
          'ApresNote is a native SwiftUI iPhone app for recording meetings, on-device transcription and speaker labeling, summaries, action items and cited answers. Optional sync, cloud text AI and Pro audio backup have separate consent controls.',
          'Only record, process or share conversations when you have the necessary permissions and consent. You are responsible for the content you record and how you use or share it.',
          'AI-generated transcripts, names, summaries and answers can contain errors. Review the recording and source text before relying on a result.'
        ]
      },
      {
        title: '2. Subscriptions and credits',
        content: [
          'Pro is an optional monthly auto-renewing App Store subscription. The current localized price and billing terms are shown through StoreKit in the app and Apple’s purchase sheet before you confirm.',
          'Apple bills your Apple Account. Subscriptions renew unless cancelled under the App Store subscription terms. Manage or cancel them in iPhone Settings, your name, Subscriptions.',
          'Deleting your ApresNote account or removing the app does not cancel an Apple subscription. Cancel it separately through Apple if you do not want it to renew.',
          'Cloud AI operations use the app’s credit system; the selected operation and thinking level determine the charge. Speech transcription is not a credit event.'
        ]
      },
      {
        title: '3. Refunds and purchase support',
        content: 'App Store purchases are processed by Apple. Refund eligibility is determined by Apple; you can request a refund at reportaproblem.apple.com or contact Apple Support. We do not process App Store refunds directly.'
      },
      {
        title: '4. Accounts and data choices',
        content: [
          'You are responsible for maintaining the security of your account credentials. Your meeting content remains yours; we claim no ownership over it.',
          'Use the app’s Settings to manage sync, cloud AI, audio backup and account deletion. The Privacy Policy explains what each choice sends or retains and how partial deletion and receipt recovery work.',
          'Cloud features depend on your consent, connectivity and service availability. Local processing depends on a supported iPhone, permissions and any required installed models.'
        ]
      },
      {
        title: '5. Acceptable use and intellectual property',
        content: [
          'Do not use ApresNote to store or share content that is illegal, harmful or violates the rights of others, or to abuse service resources.',
          `ApresNote and its associated trademarks, logos and app content are the property of ${LEGAL_ENTITY}. Your recordings and meeting content remain yours.`
        ]
      },
      {
        title: '6. Disclaimers',
        content: [
          'ApresNote is provided "as is" without warranties of any kind, express or implied.',
          'We do not guarantee uninterrupted or error-free operation. We are not responsible for data loss resulting from device failure, network issues or other circumstances beyond our control.'
        ]
      },
      {
        title: '7. Limitation of liability',
        content: `To the maximum extent permitted by law, ${LEGAL_ENTITY} shall not be liable for any indirect, incidental, special, consequential or punitive damages arising from your use of ApresNote, including loss of data, revenue or profits.`
      },
      {
        title: '8. Governing law',
        content: `These Terms are governed by the laws of ${JURISDICTION}, without regard to conflict of law principles.`
      },
      {
        title: '9. Changes and contact',
        content: `We may update these Terms and their effective date. For questions about these Terms or ApresNote, contact ${SUPPORT_EMAIL}.`
      }
    ]
  },
  support: {
    title: 'ApresNote Support',
    intro: 'Help with the ApresNote iPhone app, recordings, privacy choices and purchases.',
    contact: { email: SUPPORT_EMAIL },
    faqs: [
      {
        question: 'Does ApresNote send my recording to an AI service?',
        answer: 'Speech transcription and speaker voice matching run on your iPhone. Optional cloud AI receives text from synced meetings only after separate consent. Optional Pro audio backup stores your recording in Google Cloud Storage, but audio is never sent to Gemini or File Search.'
      },
      {
        question: 'What happens when I turn off a cloud feature?',
        answer: 'Turning off sync stops ordinary meeting uploads and offers to delete existing cloud copies. Revoking cloud AI deletes the File Search store but keeps synced records and summaries. Revoking audio backup deletes stored backup objects after confirmation. These are separate choices.'
      },
      {
        question: 'How do I delete my account or check a deletion?',
        answer: 'Use Delete Account in ApresNote Settings. If a step fails, keep the app and its local data in place and use the displayed retry or status controls. Completed deletion receipt access expires after seven days; recovery reports status only and cannot restore meeting content. Local cleanup can be retried from confirmed completion evidence already saved on the device.'
      },
      {
        question: 'Will signing out delete my recordings?',
        answer: 'Signing out preserves recordings and meeting text for that account and clears its owned voice identity. It is not account deletion. Signing into a different account does not assign the previous account’s recordings to it.'
      },
      {
        question: 'How do I manage Pro or a purchase problem?',
        answer: 'The app shows Apple’s current localized subscription price before purchase. Manage or cancel Pro in iPhone Settings, your name, Subscriptions. Account deletion and uninstalling do not cancel it. For purchase restoration problems, use Restore Purchases in the app and contact support if ownership cannot be confirmed. Apple handles refund requests.'
      },
      {
        question: 'How do calendar attendees work?',
        answer: 'ApresNote uses calendar access you grant on the iPhone. Google Calendar is optional, read-only and fetched on the device. Disconnect it in Settings to remove that source; EventKit attendees remain available under their own permission.'
      }
    ],
    troubleshooting: [
      {
        title: 'A recording or processing task is incomplete',
        steps: [
          'Keep the app and its local data in place while troubleshooting.',
          'Check microphone permissions, available device storage and the processing status shown for the meeting.',
          'Use the in-app retry or restore option when available. A backup can be restored only if one was previously saved.',
          'Contact support with the app version, iOS version and error message. Redact names and meeting content; do not attach recordings or recovery credentials.'
        ]
      }
    ],
    systemRequirements: [
      'iOS 26 or later on a supported iPhone. The device floor is iPhone 16 Pro; the app checks compatibility.',
      'Microphone permission for recording, and separate permissions for the optional calendar or contacts features you use.',
      'Space for recordings and any local models you choose to download.',
      'Internet access for sign-in, model downloads and the cloud features you enable.'
    ]
  }
} satisfies Required<Pick<ProjectPages, 'privacy' | 'terms' | 'support'>>;

// Registry of project content
export const projectContent: Record<string, ProjectPages> = {
  'apresnote': apresNoteContent,
  'clipz': {
    privacy: {
      effectiveDate: 'April 2026',
      intro: 'Clipz ("the App") is developed by Apres AI LLC, a Colorado limited liability company ("we", "us", or "our"). This Privacy Policy explains how we collect, use, and protect your information when you use Clipz.',
      sections: [
        {
          title: 'Information We Collect',
          content: [
            'Clipboard Content: When cloud sync is enabled, your clipboard history (text, images, rich text) is synced to our servers. This data is encrypted in transit and at rest.',
            'Account Information: If you sign in with Apple or Google, we receive your email address and name to identify your account.',
            'Usage Data: We may collect anonymous usage statistics to improve the app. This does not include clipboard content.'
          ]
        },
        {
          title: 'How We Use Your Information',
          content: [
            'To provide clipboard sync across your devices',
            'To maintain and improve the App',
            'To respond to support requests',
            'We never sell your data to third parties'
          ]
        },
        {
          title: 'Data Storage & Security',
          content: [
            'Your clipboard data is stored securely on AWS infrastructure in the United States.',
            'Data is encrypted using industry-standard AES-256 encryption.',
            'Your data is isolated per-user and cannot be accessed by other users.',
            'Cloud data is automatically deleted after 30 days of inactivity.'
          ]
        },
        {
          title: 'Local-First Design',
          content: 'By default, Clipz stores all data locally on your Mac. Cloud sync is entirely optional and must be explicitly enabled by signing in.'
        },
        {
          title: 'Your Rights',
          content: [
            'Access: You can view all your clipboard data within the app.',
            'Delete: You can clear your history at any time from Settings.',
            'Account Deletion: You can permanently delete your account and all associated data from Settings > Account > Delete Account.'
          ]
        },
        {
          title: 'Third-Party Services',
          content: [
            'Authentication: Apple Sign-In and Google Sign-In for account creation.',
            'Cloud Infrastructure: Amazon Web Services (AWS) for data storage and sync.',
            'No third-party analytics or advertising services are used.'
          ]
        },
        {
          title: 'Changes to This Policy',
          content: 'We may update this Privacy Policy from time to time. We will notify you of significant changes through the App or via email.'
        },
        {
          title: 'Contact Us',
          content: 'If you have questions about this Privacy Policy, contact us at support@apresai.dev.'
        }
      ]
    },
    support: {
      intro: "We're here to help you get the most out of Clipz.",
      contact: {
        email: 'support@apresai.dev',
        responseTime: 'We typically respond within 24 hours.'
      },
      faqs: [
        {
          question: 'How do I enable cloud sync?',
          answer: 'Open Clipz from the menu bar, click the gear icon to open Settings, and sign in with your Apple ID or Google account. Once signed in, cloud sync is automatically enabled.'
        },
        {
          question: 'Is my clipboard data secure?',
          answer: 'Yes! Clipz is local-first, meaning your data stays on your Mac by default. If you enable cloud sync, your data is encrypted in transit and at rest using industry-standard encryption. Your data is isolated per-user and automatically deleted after 30 days.'
        },
        {
          question: 'How do I delete my account?',
          answer: 'Open Settings in Clipz, scroll to the Account section, and click "Delete Account." This will permanently remove your account and all associated data from our servers.'
        },
        {
          question: "Why isn't Clipz capturing my clipboard?",
          answer: 'Clipz polls the system clipboard every 0.5 seconds. If items aren\'t appearing, check that Clipz is running (look for the scissors icon in your menu bar). Some apps may copy to a private clipboard that Clipz cannot access.'
        },
        {
          question: 'How do I make Clipz start automatically?',
          answer: 'Open Clipz Settings and enable "Launch at Login." Clipz will then start automatically when you log in to your Mac.'
        },
        {
          question: 'Can I use Clipz on multiple Macs?',
          answer: 'Yes! Sign in with the same Apple ID or Google account on all your Macs, and your clipboard history will sync automatically between them.'
        },
        {
          question: 'How do I clear my clipboard history?',
          answer: 'Open Clipz, click the gear icon, and select "Clear History." This will remove all items from your local history. If cloud sync is enabled, it will also delete your cloud data.'
        }
      ],
      troubleshooting: [
        {
          title: 'Cloud sync not working',
          steps: [
            'Check your internet connection',
            'Sign out and sign back in (Settings > Account)',
            'Restart Clipz (quit from menu bar, then reopen)',
            'If issues persist, contact support'
          ]
        },
        {
          title: 'Menu bar icon not appearing',
          steps: [
            'Open Clipz from Applications folder',
            'Check System Preferences > Control Center > Menu Bar Only',
            'Ensure Clipz is allowed to run in background'
          ]
        },
        {
          title: 'Images not syncing',
          steps: [
            'Large images (over 10 MB) are not synced to save bandwidth and storage',
            'A notification appears when this happens',
            'Consider using smaller screenshots or compressed images'
          ]
        }
      ],
      systemRequirements: [
        'macOS 13.0 (Ventura) or later',
        'Apple Silicon or Intel processor',
        'Less than 50 MB storage',
        'Internet required for cloud sync (optional feature)'
      ]
    },
    terms: {
      effectiveDate: 'April 2026',
      intro: 'These Terms of Service ("Terms") govern your use of Clipz ("the App"), developed by Apres AI LLC, a Colorado limited liability company ("we", "us", or "our"). By downloading or using Clipz, you agree to these Terms.',
      sections: [
        {
          title: '1. The App',
          content: 'Clipz is a macOS clipboard manager that saves your clipboard history locally on your Mac. The core app is free to download and use. Cloud sync is available as an optional paid subscription.'
        },
        {
          title: '2. Subscriptions & Pricing',
          content: [
            'Cloud Sync Monthly: $0.99/month — syncs clipboard history across your Macs in real time.',
            'Cloud Sync Annual: $7.99/year — same features as monthly, billed annually.',
            'Subscriptions are billed through your Apple ID account via the Mac App Store.',
            'Prices may vary by region and are displayed in your local currency at checkout.'
          ]
        },
        {
          title: '3. Free Trial & Auto-Renewal',
          content: [
            'Subscriptions auto-renew at the end of each billing period unless you cancel at least 24 hours before the current period ends.',
            'Your Apple ID account will be charged for renewal within 24 hours prior to the end of the current period.',
            'You can manage or cancel your subscription at any time through System Settings > Apple ID > Subscriptions on your Mac.',
            'If a free trial is offered, any unused portion is forfeited when you purchase a subscription.'
          ]
        },
        {
          title: '4. Refunds',
          content: 'All purchases are processed by Apple. To request a refund, visit reportaproblem.apple.com or contact Apple Support. We do not process refunds directly.'
        },
        {
          title: '5. Account & Data',
          content: [
            'You may sign in with Apple or Google to enable cloud sync.',
            'You are responsible for maintaining the security of your account credentials.',
            'Your clipboard data is stored locally by default. Cloud-synced data is encrypted in transit and at rest on AWS infrastructure.',
            'You can delete your account and all cloud data at any time from Settings within the app.'
          ]
        },
        {
          title: '6. Acceptable Use',
          content: [
            'You agree not to use Clipz to store or sync content that is illegal, harmful, or violates the rights of others.',
            'You agree not to reverse-engineer, decompile, or attempt to extract the source code of the App.',
            'You agree not to use the cloud sync service to circumvent storage limits or abuse system resources.',
            'We reserve the right to suspend or terminate accounts that violate these terms.'
          ]
        },
        {
          title: '7. Intellectual Property',
          content: 'Clipz and all associated trademarks, logos, and content are the property of Apres AI LLC. Your clipboard content remains yours — we claim no ownership over data you sync through the service.'
        },
        {
          title: '8. Disclaimers',
          content: [
            'Clipz is provided "as is" without warranties of any kind, express or implied.',
            'We do not guarantee uninterrupted or error-free operation of the App or cloud sync service.',
            'We are not responsible for data loss resulting from device failure, network issues, or other circumstances beyond our control.',
            'Cloud sync availability depends on your internet connection and Apple/AWS service availability.'
          ]
        },
        {
          title: '9. Limitation of Liability',
          content: 'To the maximum extent permitted by law, Apres AI LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of Clipz, including but not limited to loss of data, revenue, or profits.'
        },
        {
          title: '10. Changes to These Terms',
          content: 'We may update these Terms from time to time. We will notify you of significant changes through the App or via the email associated with your account. Continued use of the App after changes constitutes acceptance of the updated Terms.'
        },
        {
          title: '11. Governing Law',
          content: 'These Terms are governed by the laws of the State of Colorado, United States, without regard to conflict of law principles.'
        },
        {
          title: '12. Contact',
          content: 'If you have questions about these Terms, contact us at support@apresai.dev.'
        }
      ]
    }
  },

  'sophies-trophies': {
    // Sophie's Trophies has its own website, so just a landing page reference
    landing: {
      heroTitle: "Sophie's Trophies",
      heroSubtitle: 'AI-Powered Wine Discovery & Collection',
      ctaText: 'Visit Website',
      ctaUrl: 'https://sophiestrophies.com'
    }
  },

  'gimage': {
    // Gimage uses GitHub for docs, so just landing page
    landing: {
      heroTitle: 'Gimage',
      heroSubtitle: 'AI-Powered Image Generation & Processing',
      ctaText: 'View on GitHub',
      ctaUrl: 'https://github.com/apresai/gimage'
    }
  },

  'ftw': {
    landing: {
      heroTitle: 'FTW (For The Win)',
      heroSubtitle: 'Step Tracking Competitions for Friends',
      ctaText: 'Download on the App Store',
      ctaUrl: 'https://apps.apple.com/app/ftw-for-the-win/id6759205235'
    },
    privacy: {
      effectiveDate: 'April 2026',
      intro: 'FTW ("For The Win", "the App") is developed by Apres AI LLC, a Colorado limited liability company ("we", "us", or "our"). This Privacy Policy explains how we collect, use, and protect your information when you use FTW.',
      sections: [
        {
          title: 'Information We Collect',
          content: [
            'Health Data: Step counts and distance data from Apple HealthKit, used solely for leaderboard competition. This data is synced to our servers to compute rankings.',
            'Account Information: When you sign in with Apple or Google, we receive your name and email address to identify your account.',
            'Device Tokens: Push notification tokens are stored to send you competition updates. You can disable notifications at any time.',
            'Usage Data: We collect anonymous usage statistics (badge completions, group activity) to improve the app. This does not include health data details.'
          ]
        },
        {
          title: 'How We Use Your Information',
          content: [
            'To compute and display step leaderboards within your groups',
            'To award achievement badges based on your activity',
            'To send push notifications about rank changes, cheers, and milestones',
            'To maintain and improve the App',
            'We never sell your data to third parties'
          ]
        },
        {
          title: 'HealthKit Data',
          content: [
            'FTW reads step count and walking distance from Apple HealthKit.',
            'This data is used exclusively for leaderboard computation and badge progress.',
            'Health data is never shared with third parties or used for advertising.',
            'You can revoke HealthKit access at any time in Settings > Privacy > Health.'
          ]
        },
        {
          title: 'Data Storage & Security',
          content: [
            'Your data is stored securely on AWS infrastructure in the United States.',
            'Authentication tokens are encrypted and rotated automatically.',
            'Group data is isolated — only group members can see each other\'s steps.',
            'All API communication uses TLS encryption.'
          ]
        },
        {
          title: 'Your Rights',
          content: [
            'Access: You can view all your step data and badges within the app.',
            'Delete: You can delete your account and all associated data from Settings.',
            'Groups: You can leave any group at any time.',
            'Blocking: You can block other users to prevent unwanted interactions.'
          ]
        },
        {
          title: 'Contact Us',
          content: 'If you have questions about this Privacy Policy, contact us at support@apresai.dev.'
        }
      ]
    },
    support: {
      intro: "We're here to help you get the most out of FTW.",
      contact: {
        email: 'support@apresai.dev',
        responseTime: 'We typically respond within 24 hours.'
      },
      faqs: [
        {
          question: 'How do I create a group?',
          answer: 'Tap the + button on the Groups tab, enter a group name, and share the generated invite code with friends. They can join by entering the code in the app.'
        },
        {
          question: 'Why aren\'t my steps updating?',
          answer: 'Make sure you\'ve granted HealthKit permissions in Settings > Privacy > Health > FTW. Steps sync automatically in the background, but you can pull-to-refresh on the leaderboard for an immediate update.'
        },
        {
          question: 'How do badges work?',
          answer: 'FTW has 62 achievement badges across 9 categories. Some are one-time achievements (like lifetime step totals), while 36 badges are repeatable with milestone tier progression — earn them 5x for bronze, 25x for silver, and 100x for gold.'
        },
        {
          question: 'Can I use FTW without Apple Watch?',
          answer: 'Yes! FTW works with iPhone alone. Your iPhone tracks steps via the motion coprocessor. The Apple Watch companion app is optional and provides glanceable stats and complications.'
        },
        {
          question: 'How are ties handled on the leaderboard?',
          answer: 'When two members have the same step count, they share the same rank. Ties are broken alphabetically by display name, and the next rank skips accordingly.'
        },
        {
          question: 'What are cheers and taunts?',
          answer: 'Cheers are encouragement messages you can send to group members. Taunts are playful competitive messages. You can also enable auto-taunts that send automatically when you overtake someone.'
        }
      ],
      systemRequirements: [
        'iOS 17.0 or later',
        'watchOS 10.0 or later (optional)',
        'HealthKit-compatible device',
        'Internet connection for leaderboard sync'
      ]
    }
  },

  'reelz': {
    // Reelz is a fully local app — no cloud, no account, no network calls. The site-wide /privacy
    // page describes Google Sign-In + AWS, which do NOT apply here, so Reelz gets its own page that
    // doubles as the as-is / no-warranty disclaimer.
    privacy: {
      effectiveDate: 'June 2026',
      intro: 'Reelz ("the App") is developed by Apres AI LLC, a Colorado limited liability company ("we", "us", or "our"). Reelz is a free, fully local macOS screen recorder. This page explains how Reelz handles your data and the terms under which it is provided.',
      sections: [
        {
          title: 'No Data Collection',
          content: [
            'Reelz does not collect, transmit, or store any personal data.',
            'There is no account, no sign-in, and no cloud component.',
            'Reelz makes no network connections to our servers — all recording and encoding happen entirely on your Mac.',
            'There is no analytics, telemetry, advertising, or crash reporting of any kind.'
          ]
        },
        {
          title: 'Your Recordings Stay on Your Mac',
          content: [
            'Clips you record are saved only to the folder you choose on your own computer.',
            'Reelz never uploads, copies, or shares your recordings anywhere.',
            'You are solely in control of where clips are stored and who you share them with.'
          ]
        },
        {
          title: 'Permissions',
          content: [
            'Reelz requires macOS Screen Recording permission to capture your screen or a window.',
            'This permission is managed entirely by macOS in System Settings > Privacy & Security > Screen Recording.',
            'Reelz requests no other permissions — no camera, microphone, contacts, photos, or network access.'
          ]
        },
        {
          title: 'Disclaimer & No Warranty',
          content: [
            'Reelz is provided free of charge, "as is" and "as available", without warranties of any kind, express or implied, including but not limited to the implied warranties of merchantability, fitness for a particular purpose, and non-infringement.',
            'To the maximum extent permitted by law, Apres AI LLC shall not be liable for any damages whatsoever — including, without limitation, loss of data or recordings, or any direct, indirect, incidental, or consequential damages — arising out of the use of or inability to use Reelz. You use it at your own risk.',
            'You are responsible for ensuring your use of Reelz complies with all applicable laws and respects the privacy of anyone who appears in your recordings.'
          ]
        },
        {
          title: 'Changes to This Page',
          content: 'We may update this page from time to time. Material changes will be reflected here with an updated effective date.'
        },
        {
          title: 'Contact',
          content: 'If you have questions about this page, contact us at support@apresai.dev.'
        }
      ]
    }
  }
};

// Helper to get content for a project
export const getProjectContent = (slug: string): ProjectPages | undefined => {
  return projectContent[slug];
};

// Helper to check if a project has a specific page
export const hasProjectPage = (slug: string, page: 'privacy' | 'support' | 'landing' | 'terms'): boolean => {
  const content = projectContent[slug];
  if (!content) return false;
  return content[page] !== undefined;
};
